import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Radio,
  TextField,
  Tooltip,
  InputAdornment
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

interface SettingLineProps {
  settingsMetadataId: number
  settingsValuesId: number
  productOriginId: number | null
  dataType: 'bool' | 'string' | 'number'
  name: string
  description: string
  toolTip: string
  value: string
  isActive: boolean
  isConflict?: boolean
  forcedValue?: string
  pendingValue?: string
  onChange?: (
    settingsValuesId: number,
    newValue: string,
    productOriginId: number | null,
    isDirty: boolean
  ) => void
}

export const SettingLine = ({
  settingsValuesId,
  productOriginId,
  dataType,
  name,
  description,
  toolTip,
  value,
  isActive,
  isConflict,
  forcedValue,
  pendingValue,
  onChange
}: SettingLineProps) => {
  const [currentValue, setCurrentValue] = useState(value)

  // Prioridad: forcedValue > pendingValue (del padre) > currentValue (local)
  const activeValue =
    forcedValue !== undefined
      ? forcedValue
      : pendingValue !== undefined
        ? pendingValue
        : currentValue
  const isDirty = activeValue !== value

  // Solo notifica el cambio a pendingChanges cuando se aplica un forcedValue
  useEffect(() => {
    if (forcedValue !== undefined && forcedValue !== value) {
      onChange?.(settingsValuesId, forcedValue, productOriginId, true)
    }
  }, [forcedValue])

  const tooltipParts = toolTip?.includes('|')
    ? toolTip.split('|').map((t) => t.trim())
    : null

  // ── Bool ────────────────────────────────────────────────────────────────────
  if (dataType === 'bool') {
    const handleBoolChange = (newValue: string) => {
      setCurrentValue(newValue)
      onChange?.(
        settingsValuesId,
        newValue,
        productOriginId,
        newValue !== value
      )
    }

    const makeRadio = (radioValue: string, tip?: string) => {
      const radio = (
        <Radio
          size='small'
          checked={activeValue === radioValue}
          onChange={() => handleBoolChange(radioValue)}
          disabled={!isActive}
          sx={{
            color: 'grey.400',
            '&.Mui-checked': { color: 'success.main' }
          }}
        />
      )

      return tip ? (
        <Tooltip title={tip} arrow placement='top'>
          <span>{radio}</span>
        </Tooltip>
      ) : (
        radio
      )
    }

    return (
      <Box
        display='grid'
        gridTemplateColumns='1fr 100px 100px'
        alignItems='center'
        px={3}
        py={1.5}
        sx={{
          opacity: isActive ? 1 : 0.6,
          bgcolor: isConflict ? '#ffebee' : isDirty ? '#fff3e0' : 'transparent',
          transition: 'background-color 0.2s'
        }}
      >
        <Box>
          <Typography variant='body2' fontWeight={500}>
            {name}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {description}
          </Typography>
        </Box>

        <Box display='flex' justifyContent='center'>
          {makeRadio('true', tooltipParts?.[0])}
        </Box>

        <Box display='flex' justifyContent='center'>
          {makeRadio('false', tooltipParts?.[1])}
        </Box>
      </Box>
    )
  }

  // ── String / Number ─────────────────────────────────────────────────────────
  return (
    <Box
      display='grid'
      gridTemplateColumns='1fr 200px'
      alignItems='center'
      px={3}
      py={1.5}
      sx={{
        opacity: isActive ? 1 : 0.6,
        bgcolor: isDirty ? '#fff3e0' : 'transparent',
        transition: 'background-color 0.2s'
      }}
    >
      <Box>
        <Typography variant='body2' fontWeight={500}>
          {name}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {description}
        </Typography>
      </Box>

      <Box display='flex' justifyContent='center' alignItems='center'>
        <TextField
          size='small'
          type={dataType === 'number' ? 'number' : 'text'}
          value={activeValue}
          disabled={!isActive}
          onChange={(e) => {
            setCurrentValue(e.target.value)
            onChange?.(
              settingsValuesId,
              e.target.value,
              productOriginId,
              e.target.value !== value
            )
          }}
          sx={{ width: '100%' }}
          InputProps={
            toolTip && !tooltipParts
              ? {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Tooltip title={toolTip} arrow placement='top'>
                        <InfoOutlinedIcon
                          fontSize='small'
                          color='action'
                          sx={{ cursor: 'default' }}
                        />
                      </Tooltip>
                    </InputAdornment>
                  )
                }
              : undefined
          }
        />
      </Box>
    </Box>
  )
}
